/**
 * /quiz-buzzer — the referee. Pure logic, no DOM, no network, no clock of its
 * own: every call takes `now` (host time, ms) so the whole game can be replayed
 * in a test with simulated phones.
 *
 * A press arrives already translated into host time (clock.js). The engine
 * decides what it means:
 *
 *   valid      — after the opening, player not locked: goes into the order;
 *   false      — before the opening (or while the buttons are closed): a false
 *                start, penalised by the preset (a timed lockout, the loss of
 *                this question, or nothing);
 *   locked     — during that player's lockout;
 *   out        — the player (or their team) already lost this question;
 *   late       — ЧГК: after the minute, when answers are being handed in;
 *   ignored    — a stray tap right after the previous question closed.
 *
 * The winner is NOT the first press to arrive. The first valid press starts a
 * grace window measured from its own press time; any press made earlier by a
 * slower phone lands inside that window (its uplink is bounded by its round
 * trip — clock.js graceFor), and when the window closes the earliest press
 * time wins. Presses that still arrive later are kept and flagged `late`, so
 * the host sees them instead of silently losing them.
 *
 * Teams: in team scoring a player's presses, lockouts and points belong to the
 * team («entity» below), the way a team button works in brain-ring.
 */

export const PRESETS = {
    // «Своя игра» / Jeopardy: each player for themselves, points = question value,
    // a wrong answer costs the value and the buttons reopen for the others.
    jeopardy: {
        scoring: 'players', base: 100, round: 1, value: 100, wrongSubtracts: true,
        falseStart: 'lockout', lockoutMs: 1000, openSec: 0, answerSec: 7,
        afterWrong: 'rearm', collectAll: false, warnSec: 0, extraSec: 0
    },
    // «Что? Где? Когда?»: a minute of team discussion, a signal ten seconds before
    // the end, then ten seconds to hand in the answer. Buttons collect the order of
    // early answers without stopping the minute.
    chgk: {
        scoring: 'teams', base: 1, round: 1, value: 1, wrongSubtracts: false,
        falseStart: 'none', lockoutMs: 0, openSec: 60, answerSec: 0,
        afterWrong: 'next', collectAll: true, warnSec: 10, extraSec: 10
    },
    // Brain-ring: two teams, 20 s after the signal; a false start takes the
    // question away from that team; after a wrong answer the other team gets
    // what is left of the 20 s.
    brainring: {
        scoring: 'teams', base: 1, round: 1, value: 1, wrongSubtracts: false,
        falseStart: 'question', lockoutMs: 0, openSec: 20, answerSec: 0,
        afterWrong: 'rearm', collectAll: false, warnSec: 5, extraSec: 0
    },
    free: {
        scoring: 'players', base: 1, round: 1, value: 1, wrongSubtracts: false,
        falseStart: 'lockout', lockoutMs: 500, openSec: 0, answerSec: 0,
        afterWrong: 'rearm', collectAll: false, warnSec: 0, extraSec: 0
    }
};

export const COOLDOWN_MS = 900;   // a double tap on the last answer is not a false start

function clone(o) { return JSON.parse(JSON.stringify(o)); }

export function createEngine(opts) {
    opts = opts || {};
    const listeners = [];
    const graceFn = typeof opts.grace === 'function' ? opts.grace : function () { return opts.grace || 0; };
    const settings = Object.assign({ preset: 'free' }, clone(PRESETS.free), opts.settings || {});
    const players = new Map();      // pid -> player
    let teams = [];                 // [{id, name, color}]
    const scores = new Map();       // entity -> points
    const history = [];             // [{ent, delta, qid, why}]
    let phase = 'closed';           // closed | armed | answering | extra
    let timer = null;               // {kind: open|answer|extra, endsAt, total, warned, fired}
    let qseq = 0;
    let q = newQ(opts.now || 0, true);
    let prev = null;                // the question that just ended, for the host's view
    let judged = 0;

    function emit(type, data) {
        for (const fn of listeners.slice()) { try { fn(type, data || {}); } catch (e) { /* a listener must not break the referee */ } }
    }

    function newQ(now, first) {
        return {
            id: ++qseq, round: 0, presses: [], answerer: null, answererEnt: null,
            locked: new Map(), armAt: null, declareAt: null, declaredAt: null,
            done: false, openLeft: null, results: [],
            cooldownUntil: first ? -Infinity : now + COOLDOWN_MS, startedAt: now
        };
    }

    function entityOf(pid) {
        const p = players.get(pid);
        if (!p) return 'p:' + pid;
        if (settings.scoring === 'teams' && p.team && teams.some(function (t) { return t.id === p.team; })) return 't:' + p.team;
        return 'p:' + pid;
    }
    function entityInfo(ent) {
        if (ent.startsWith('t:')) {
            const t = teams.find(function (x) { return 't:' + x.id === ent; });
            return t ? { ent: ent, name: t.name, color: t.color, team: true } : { ent: ent, name: '?', color: '#888', team: true };
        }
        const p = players.get(ent.slice(2));
        return p ? { ent: ent, name: p.name, color: p.color, team: false } : { ent: ent, name: '?', color: '#888', team: false };
    }

    /** Every entity that could still answer this question (online players, local players). */
    function liveEntities() {
        const out = new Set();
        players.forEach(function (p, pid) { if (p.online !== false) out.add(entityOf(pid)); });
        return out;
    }
    function eligibleLeft() {
        const live = liveEntities();
        if (!live.size) return false;
        for (const e of live) if (!q.locked.has(e)) return true;
        return false;
    }

    /** First valid press of each entity in the current round, earliest first. */
    function rankedFirsts() {
        const seen = new Map();
        for (const r of q.presses) {
            if (r.kind !== 'valid' || r.round !== q.round) continue;
            const cur = seen.get(r.ent);
            if (!cur || before(r, cur)) seen.set(r.ent, r);
        }
        const list = Array.from(seen.values()).sort(function (a, b) { return before(a, b) ? -1 : before(b, a) ? 1 : 0; });
        const lead = list[0];
        list.forEach(function (r, i) {
            r.rank = i + 1;
            r.gap = lead ? r.t - lead.t : 0;
            const pv = list[i - 1];
            // Closer than the measurements can reliably tell apart: say so instead
            // of pretending one of them was clearly first. The error bars are
            // worst-case bounds; half their sum is where a flip becomes plausible.
            r.tie = !!pv && (r.t - pv.t) <= Math.max(3, (finiteErr(r.err) + finiteErr(pv.err)) / 2);
        });
        return list;
    }
    function finiteErr(e) { return Number.isFinite(e) ? e : 0; }
    function before(a, b) {
        if (a.t !== b.t) return a.t < b.t;
        if (a.recvAt !== b.recvAt) return a.recvAt < b.recvAt;
        return (a.seq || 0) < (b.seq || 0);
    }

    function startOpenTimer() {
        const left = q.openLeft != null ? q.openLeft : (settings.openSec > 0 ? settings.openSec * 1000 : null);
        if (left == null) { timer = null; return; }
        timer = { kind: 'open', endsAt: q.armAt + left, total: settings.openSec * 1000,
                  warned: settings.warnSec > 0 && left <= settings.warnSec * 1000, fired: false };
    }

    function arm(now, lead) {
        if (phase === 'armed' || phase === 'answering') return false;
        // A question whose time ran out is over: the next opening is a new question
        // (and its locks, lockouts and order start empty).
        if (phase === 'extra' || q.expired || q.openLeft === 0) finish(now, 'timeout');
        q.round++;
        q.armAt = now + Math.max(0, lead || 0);
        q.declareAt = null;
        q.answerer = null; q.answererEnt = null;
        phase = 'armed';
        startOpenTimer();
        emit('armed', { armAt: q.armAt, lead: lead || 0, qid: q.id, round: q.round });
        return true;
    }

    function close(now) {
        if (phase !== 'armed') return false;
        if (q.declareAt != null && !q.answerer) { declare(now, true); if (phase !== 'armed') return true; }
        if (timer && timer.kind === 'open') q.openLeft = Math.max(0, timer.endsAt - Math.max(now, q.armAt));
        timer = null;
        phase = 'closed';
        emit('closed', { qid: q.id });
        return true;
    }

    function setAnswerer(rec, now, how) {
        q.answerer = rec.pid; q.answererEnt = rec.ent; q.declareAt = null; q.declaredAt = now;
        if (!settings.collectAll) {
            if (timer && timer.kind === 'open') q.openLeft = Math.max(0, timer.endsAt - Math.max(rec.t, q.armAt));
            phase = 'answering';
            timer = settings.answerSec > 0 ? { kind: 'answer', endsAt: now + settings.answerSec * 1000, total: settings.answerSec * 1000, fired: false } : null;
        }
        emit('declared', { pid: rec.pid, ent: rec.ent, t: rec.t, rel: rec.t - q.armAt, how: how || 'first', qid: q.id });
    }

    function declare(now, force) {
        if (q.answerer) return;
        if (!force && (q.declareAt == null || now < q.declareAt)) return;
        const list = rankedFirsts().filter(function (r) { return !q.locked.has(r.ent); });
        q.declareAt = null;
        if (!list.length) return;
        setAnswerer(list[0], now, 'first');
    }

    function finish(now, why) {
        timer = null;
        phase = 'closed';
        q.done = true; q.endedAt = now; q.why = why;
        rankedFirsts();
        prev = q;
        q = newQ(now, false);
        emit('question_done', { why: why, qid: prev.id });
    }

    function timeUp(now) {
        if (q.declareAt != null && !q.answerer) declare(now, true);
        if (phase !== 'armed') return;
        if (settings.extraSec > 0) {
            phase = 'extra';
            timer = { kind: 'extra', endsAt: now + settings.extraSec * 1000, total: settings.extraSec * 1000, fired: false };
            emit('time_up', { extra: true, qid: q.id });
        } else {
            timer = null; q.openLeft = 0; phase = 'closed';
            emit('time_up', { extra: false, qid: q.id, pressed: q.presses.some(function (r) { return r.kind === 'valid'; }) });
        }
    }

    function tick(now) {
        if (phase === 'armed') {
            if (q.declareAt != null && !q.answerer && now >= q.declareAt) declare(now, false);
        }
        if (phase === 'armed' && timer && timer.kind === 'open') {
            const left = timer.endsAt - now;
            if (!timer.warned && settings.warnSec > 0 && left <= settings.warnSec * 1000 && now >= q.armAt) {
                timer.warned = true; emit('warn', { left: left });
            }
            if (left <= 0) timeUp(now);
        } else if (phase === 'answering' && timer && timer.kind === 'answer' && !timer.fired && now >= timer.endsAt) {
            timer.fired = true; emit('answer_time_up', { pid: q.answerer });
        } else if (phase === 'extra' && timer && now >= timer.endsAt) {
            emit('final', { qid: q.id });
            timer = null; phase = 'closed'; q.expired = true;
        }
    }

    /** When the next thing will happen on its own — the runtime schedules a timer for it. */
    function nextDeadline() {
        const c = [];
        if (phase === 'armed' && q.declareAt != null && !q.answerer) c.push(q.declareAt);
        if (timer && !timer.fired) c.push(timer.endsAt);
        if (phase === 'armed' && timer && timer.kind === 'open' && !timer.warned && settings.warnSec > 0) c.push(timer.endsAt - settings.warnSec * 1000);
        return c.length ? Math.min.apply(null, c) : null;
    }

    function press(ev, now) {
        const pl = players.get(ev.pid);
        if (!pl) return { kind: 'unknown' };
        if (ev.seq != null && q.presses.some(function (r) { return r.pid === ev.pid && r.seq === ev.seq; })) return { kind: 'dup' };
        const ent = entityOf(ev.pid);
        const rec = { pid: ev.pid, ent: ent, seq: ev.seq, t: ev.t, err: ev.err || 0, recvAt: now, how: ev.how || 'local',
                      claimed: ev.claimed, round: q.round, qid: q.id, kind: null, rel: q.armAt != null ? ev.t - q.armAt : null };
        if (phase === 'extra') rec.kind = 'late';
        else if (phase === 'closed' && ev.t < q.cooldownUntil) rec.kind = 'ignored';
        else if (q.locked.has(ent)) rec.kind = 'out';
        else if (pl.lockUntil > ev.t) rec.kind = 'locked';
        else if (phase === 'closed' || q.armAt == null || ev.t < q.armAt) {
            rec.kind = 'false';
            if (settings.falseStart === 'lockout' && settings.lockoutMs > 0) {
                pl.lockUntil = ev.t + settings.lockoutMs; rec.lockUntil = pl.lockUntil;
            } else if (settings.falseStart === 'question') {
                q.locked.set(ent, 'false');
            }
        } else {
            rec.kind = 'valid';
            if (q.answerer) rec.after = true;
        }
        if (rec.kind === 'ignored') { emit('ignored', rec); return rec; }
        q.presses.push(rec);
        if (rec.kind === 'valid') {
            if (phase === 'armed' && !q.answerer) {
                const firsts = rankedFirsts().filter(function (r) { return !q.locked.has(r.ent); });
                if (firsts.length) q.declareAt = firsts[0].t + Math.max(0, graceFn());
            } else if (q.answerer) {
                const ans = q.presses.find(function (r) { return r.kind === 'valid' && r.round === q.round && r.pid === q.answerer; });
                if (ans && ev.t < ans.t && rec.ent !== q.answererEnt) rec.late = true;
            }
            rankedFirsts();
        }
        emit('press', rec);
        tick(now);
        return rec;
    }

    function addScore(ent, delta, why) {
        if (!delta) return;
        scores.set(ent, (scores.get(ent) || 0) + delta);
        history.push({ ent: ent, delta: delta, qid: q.id, why: why });
        if (history.length > 500) history.shift();
        emit('score', { ent: ent, delta: delta, why: why });
    }

    function judge(verdict, now, lead) {
        if (!q.answerer) return false;
        const pid = q.answerer, ent = q.answererEnt;
        const value = Number(settings.value) || 0;
        judged++;
        if (verdict === 'correct') {
            q.results.push({ pid: pid, ent: ent, verdict: 'correct' });
            addScore(ent, value, 'correct');
            emit('judged', { verdict: 'correct', pid: pid, ent: ent, delta: value });
            finish(now, 'correct');
            return true;
        }
        const delta = settings.wrongSubtracts ? -value : 0;
        q.results.push({ pid: pid, ent: ent, verdict: 'wrong' });
        addScore(ent, delta, 'wrong');
        q.locked.set(ent, 'wrong');
        q.answerer = null; q.answererEnt = null; q.declareAt = null;
        emit('judged', { verdict: 'wrong', pid: pid, ent: ent, delta: delta });
        if (!eligibleLeft()) { finish(now, 'all_wrong'); return true; }
        const mode = settings.afterWrong;
        if (mode === 'next' || settings.collectAll) {
            const nx = rankedFirsts().find(function (r) { return !q.locked.has(r.ent); });
            if (nx) { setAnswerer(nx, now, 'next'); return true; }
            if (settings.collectAll) return true;       // the minute is still running
        }
        if (mode === 'close') { phase = 'closed'; timer = null; emit('closed', { qid: q.id }); return true; }
        if (q.openLeft === 0) { phase = 'closed'; timer = null; emit('time_up', { extra: false, qid: q.id }); return true; }
        phase = 'closed'; timer = null;
        arm(now, lead);
        return true;
    }

    function pass(pid, now) {
        const p = players.get(pid);
        if (!p) return false;
        const ent = entityOf(pid);
        q.locked.delete(ent);
        let rec = q.presses.find(function (r) { return r.pid === pid && r.kind === 'valid' && r.round === q.round; });
        if (!rec) rec = { pid: pid, ent: ent, t: now, err: 0, recvAt: now, round: q.round, kind: 'host' };
        if (phase === 'closed' && q.armAt == null) q.armAt = now;
        if (settings.collectAll && phase !== 'armed') phase = 'armed';
        setAnswerer(rec, now, 'host');
        return true;
    }

    function unlock(pid) {
        const p = players.get(pid);
        if (!p) return false;
        p.lockUntil = 0;
        q.locked.delete(entityOf(pid));
        emit('unlocked', { pid: pid });
        return true;
    }

    function standings() {
        const ents = new Set();
        if (settings.scoring === 'teams') teams.forEach(function (t) { ents.add('t:' + t.id); });
        players.forEach(function (p, pid) { ents.add(entityOf(pid)); });
        const list = Array.from(ents).map(function (e) {
            const info = entityInfo(e);
            info.score = scores.get(e) || 0;
            return info;
        });
        list.sort(function (a, b) { return b.score - a.score || a.name.localeCompare(b.name); });
        let place = 0, last = null;
        list.forEach(function (r, i) { if (r.score !== last) { place = i + 1; last = r.score; } r.place = place; });
        return list;
    }

    function viewFor(pid, now) {
        const p = players.get(pid);
        if (!p) return { state: 'gone' };
        const ent = entityOf(pid);
        const st = standings();
        const me = st.find(function (r) { return r.ent === ent; }) || { score: 0, place: 0 };
        const v = {
            qid: q.id, phase: phase, armAt: phase === 'armed' ? q.armAt : null,
            timer: timer ? { kind: timer.kind, endsAt: timer.endsAt, total: timer.total } : null,
            score: me.score, place: me.place, of: st.length, value: settings.value,
            ent: ent, entName: entityInfo(ent).name, team: ent.startsWith('t:'),
            answerer: q.answerer ? entityInfo(q.answererEnt).name : null
        };
        const firsts = rankedFirsts();
        const mine = firsts.find(function (r) { return r.ent === ent; });
        const myLast = q.presses.filter(function (r) { return r.pid === pid; }).pop();
        if (phase === 'extra') v.state = 'extra';
        else if (q.locked.has(ent)) { v.state = 'out'; v.reason = q.locked.get(ent); }
        else if (p.lockUntil > now) { v.state = 'penalty'; v.lockUntil = p.lockUntil; }
        else if (q.answerer && q.answererEnt === ent) v.state = q.answerer === pid ? 'first' : 'team_first';
        else if (mine && (q.answerer || phase === 'answering')) {
            v.state = 'rank'; v.rank = mine.rank; v.gap = mine.gap; v.tie = mine.tie; v.late = !!mine.late;
        }
        else if (phase === 'armed' && mine) v.state = 'pressed';
        else if (phase === 'armed') v.state = 'armed';
        else if (phase === 'answering') v.state = 'wait';
        else v.state = 'closed';
        if (myLast && myLast.kind === 'false') { v.falseAt = myLast.rel; }
        return v;
    }

    function addPlayer(o) {
        const pid = String(o.pid);
        const cur = players.get(pid);
        const p = Object.assign(cur || { score: 0, lockUntil: 0, online: true, kind: 'remote' }, {
            pid: pid, name: String(o.name || '').slice(0, 24) || '?', color: o.color || '#60a5fa',
            team: o.team || null, kind: o.kind || (cur && cur.kind) || 'remote'
        });
        if (o.key != null) p.key = o.key;
        if (o.online != null) p.online = !!o.online;
        players.set(pid, p);
        emit('roster', { pid: pid });
        return p;
    }

    function removePlayer(pid) {
        pid = String(pid);
        if (!players.has(pid)) return false;
        const ent = entityOf(pid);
        players.delete(pid);
        if (q.answerer === pid) { q.answerer = null; q.answererEnt = null; if (phase === 'answering') { phase = 'closed'; timer = null; } }
        if (ent.startsWith('p:')) scores.delete(ent);
        emit('roster', { pid: pid, removed: true });
        return true;
    }

    function setOnline(pid, on) {
        const p = players.get(String(pid));
        if (!p || p.online === !!on) return;
        p.online = !!on;
        emit('roster', { pid: String(pid), online: !!on });
    }

    function setTeams(list) {
        teams = (list || []).map(function (t) { return { id: String(t.id), name: String(t.name || '').slice(0, 24) || '?', color: t.color || '#60a5fa' }; });
        players.forEach(function (p) { if (p.team && !teams.some(function (t) { return t.id === p.team; })) p.team = null; });
        emit('roster', { teams: true });
    }

    function configure(patch) {
        Object.assign(settings, patch || {});
        emit('settings', clone(settings));
    }

    function applyPreset(name) {
        const pr = PRESETS[name];
        if (!pr) return false;
        Object.assign(settings, clone(pr), { preset: name });
        emit('settings', clone(settings));
        return true;
    }

    function adjust(ent, delta) { addScore(ent, delta, 'manual'); }

    function undo() {
        const h = history.pop();
        if (!h) return null;
        scores.set(h.ent, (scores.get(h.ent) || 0) - h.delta);
        emit('score', { ent: h.ent, delta: -h.delta, why: 'undo' });
        return h;
    }

    function resetScores() {
        scores.clear(); history.length = 0;
        emit('score', { reset: true });
    }

    function newQuestion(now) {
        players.forEach(function (p) { p.lockUntil = 0; });
        finish(now, 'reset');
    }

    function toJSON() {
        const ps = [];
        players.forEach(function (p) { ps.push({ pid: p.pid, name: p.name, color: p.color, team: p.team, kind: p.kind, key: p.key }); });
        return { v: 1, settings: clone(settings), teams: clone(teams), players: ps,
                 scores: Array.from(scores.entries()), history: history.slice(-100) };
    }
    function fromJSON(o) {
        if (!o || o.v !== 1) return false;
        Object.assign(settings, o.settings || {});
        teams = o.teams || [];
        players.clear();
        (o.players || []).forEach(function (p) { players.set(String(p.pid), Object.assign({ score: 0, lockUntil: 0, online: p.kind !== 'remote' }, p)); });
        scores.clear();
        (o.scores || []).forEach(function (e) { scores.set(e[0], e[1]); });
        history.length = 0; (o.history || []).forEach(function (h) { history.push(h); });
        emit('roster', {}); emit('settings', clone(settings)); emit('score', { restored: true });
        return true;
    }

    return {
        on: function (fn) { listeners.push(fn); return function () { const i = listeners.indexOf(fn); if (i >= 0) listeners.splice(i, 1); }; },
        press: press, tick: tick, arm: arm, close: close, judge: judge, pass: pass, unlock: unlock,
        newQuestion: newQuestion, nextDeadline: nextDeadline,
        addPlayer: addPlayer, removePlayer: removePlayer, setOnline: setOnline, setTeams: setTeams,
        configure: configure, applyPreset: applyPreset, adjust: adjust, undo: undo, resetScores: resetScores,
        standings: standings, viewFor: viewFor, entityOf: entityOf, entityInfo: entityInfo,
        rankedFirsts: rankedFirsts, toJSON: toJSON, fromJSON: fromJSON,
        get phase() { return phase; },
        get timer() { return timer ? Object.assign({}, timer) : null; },
        get question() { return q; },
        get previous() { return prev; },
        get settings() { return settings; },
        get teams() { return teams.slice(); },
        get players() { return players; },
        get judgedCount() { return judged; },
        score: function (ent) { return scores.get(ent) || 0; }
    };
}
